import { Camera, ClipboardCheck, FileSearch, ShieldCheck, Check } from "lucide-react";

/**
 * Inspection scene — property outline with checklist, camera/doc icons,
 * scan line, and 4 steps (Inspect → Document → Verify → Report).
 */
export default function PropertyInspectionVisual() {
  return (
    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-zinc-200 bg-[#fafafa] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.12)]">
      <div className="absolute inset-0 btr-blueprint-overlay opacity-70" />

      <div className="absolute top-3 left-3 text-[10px] font-display tracking-[0.3em] uppercase text-[#a2020b]">
        Inspection · Field Workflow
      </div>

      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
        <g fill="none" stroke="#a2020b" strokeWidth="1.4">
          {/* Property outline */}
          <path d="M 100 420 L 100 220 L 180 220 L 180 150 L 420 150 L 420 220 L 500 220 L 500 420 Z" strokeWidth="2" />
          <path d="M 180 150 L 300 70 L 420 150" strokeDasharray="4 4" opacity="0.7" />
          <path d="M 140 280 L 180 280" strokeWidth="2.2" />
          <path d="M 220 280 L 260 280" strokeWidth="2.2" />
          <path d="M 340 280 L 380 280" strokeWidth="2.2" />
          <path d="M 440 280 L 480 280" strokeWidth="2.2" />
          <path d="M 280 420 L 280 370 L 320 370 L 320 420" strokeWidth="1.8" />
          <path d="M 100 350 L 500 350" opacity="0.35" />
          <path d="M 300 220 L 300 420" opacity="0.3" />
        </g>
      </svg>

      {/* Camera markers */}
      {[
        { top: "26%", left: "18%" },
        { top: "26%", left: "62%" },
        { top: "50%", left: "40%" },
        { top: "64%", left: "22%" },
        { top: "64%", left: "58%" },
      ].map((c, i) => (
        <div
          key={i}
          className="absolute"
          style={{ top: c.top, left: c.left, animation: `btr-float 4s ease-in-out ${i * 0.3}s infinite` }}
        >
          <div className="w-7 h-7 rounded-md bg-[#a2020b]/10 border border-[#a2020b]/50 flex items-center justify-center">
            <Camera className="w-3.5 h-3.5 text-[#a2020b]" />
          </div>
        </div>
      ))}

      {/* Checklist card (right side) */}
      <div className="absolute top-8 right-6 w-56 bg-white rounded-md border border-zinc-200 shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-display">Inspection Checklist</div>
        <div className="h-[1px] bg-zinc-200 mt-2" />
        <ul className="mt-3 space-y-2">
          {[
            { label: "Exterior envelope", done: true },
            { label: "Roof & flashing", done: true },
            { label: "MEP systems", done: true },
            { label: "Interior finishes", done: false },
            { label: "Exhibit capture", done: false },
          ].map((r, i) => (
            <li key={i} className="flex items-center gap-2 text-[11px] font-body">
              <span
                className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center border ${
                  r.done ? "bg-[#a2020b] border-[#a2020b]" : "bg-white border-zinc-300"
                }`}
              >
                {r.done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </span>
              <span className={r.done ? "text-zinc-600 line-through" : "text-zinc-700"}>{r.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Steps strip (bottom) */}
      <div className="absolute bottom-4 left-4 right-4 flex items-stretch gap-3">
        {[
          { icon: FileSearch, label: "Inspect" },
          { icon: Camera, label: "Document" },
          { icon: ClipboardCheck, label: "Verify" },
          { icon: ShieldCheck, label: "Report" },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className="flex-1 bg-white rounded-md border border-zinc-200 px-3 py-2 flex items-center gap-2 relative overflow-hidden"
              style={{ animation: `btr-float 5s ease-in-out ${i * 0.4}s infinite` }}
            >
              <div className="w-6 h-6 rounded bg-[#a2020b]/10 border border-[#a2020b]/30 flex items-center justify-center">
                <Icon className="w-3 h-3 text-[#a2020b]" />
              </div>
              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-display">0{i + 1}</div>
                <div className="text-[11px] font-display font-bold text-[#111114] leading-none">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-[2px] bg-[#a2020b] opacity-70"
        style={{ animation: "btr-scan 5s ease-in-out infinite", boxShadow: "0 0 14px #a2020b" }}
      />
    </div>
  );
}
